import { useRef, useState, type FormEvent, type PointerEvent } from 'react';
import { useT } from '../i18n';
import { uiSocket } from '../live/socket';
import { useLive } from '../live/store';
import { sendCmd } from '../lib/robot';

/**
 * Remote control of the robot screen (PROTOCOL §17). Rendered over the Screen video while "Control" is on:
 * click → tap, drag → swipe, press ≥ 600 ms → longpress (coordinates normalized to the displayed video,
 * respecting letterboxing), plus Back / Home / Recent / keyboard. Messages go out as `{type:"input"}` on /ws/ui.
 */

type InputMsg =
  | { kind: 'tap'; x: number; y: number }
  | { kind: 'swipe'; x: number; y: number; x2: number; y2: number; ms: number }
  | { kind: 'longpress'; x: number; y: number; ms: number }
  | { kind: 'text'; text: string }
  | { kind: 'key'; key: 'BACK' | 'HOME' | 'APP_SWITCH' | 'ENTER' | 'DEL' | 'VOLUME_UP' | 'VOLUME_DOWN' };

function sendInput(serial: string, msg: InputMsg) {
  return uiSocket.send({ type: 'input', serial, ...msg });
}

const LONG_MS = 600;
const MOVE_PX = 10;
const r3 = (n: number) => Math.round(n * 1000) / 1000;

interface Ripple {
  id: number;
  x: number;
  y: number;
  kind: 'tap' | 'long' | 'swipe';
  x2?: number;
  y2?: number;
}
let rippleSeq = 1;

/** Map a point inside the overlay to 0..1 of the robot screen (object-fit: contain letterboxing). */
function normalize(overlay: HTMLElement, clientX: number, clientY: number): [number, number] | null {
  const r = overlay.getBoundingClientRect();
  const video = overlay.parentElement?.querySelector<HTMLVideoElement>('video[data-source="screen"]');
  const vw = video?.videoWidth || 1920;
  const vh = video?.videoHeight || 1080;
  const scale = Math.min(r.width / vw, r.height / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  const ox = (r.width - dw) / 2;
  const oy = (r.height - dh) / 2;
  const x = (clientX - r.left - ox) / dw;
  const y = (clientY - r.top - oy) / dh;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return [r3(x), r3(y)];
}

export function ScreenControl({ serial }: { serial: string }) {
  const t = useT();
  const input = useLive((s) => (s.robots[serial]?.status as { input?: string } | null | undefined)?.input);
  const ref = useRef<HTMLDivElement>(null);
  const start = useRef<{ t: number; x: number; y: number; nx: number; ny: number; id: number } | null>(null);
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const [kbd, setKbd] = useState(false);
  const [text, setText] = useState('');
  const [pairing, setPairing] = useState(false);

  const ripple = (rp: Omit<Ripple, 'id'>) => {
    const id = rippleSeq++;
    setRipples((l) => [...l.slice(-6), { ...rp, id }]);
    setTimeout(() => setRipples((l) => l.filter((x) => x.id !== id)), 650);
  };
  const local = (e: { clientX: number; clientY: number }) => {
    const r = ref.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };

  const onDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const n = normalize(e.currentTarget, e.clientX, e.clientY);
    if (!n) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic pointer */
    }
    const [x, y] = local(e);
    start.current = { t: performance.now(), x, y, nx: n[0], ny: n[1], id: e.pointerId };
  };
  const onUp = (e: PointerEvent<HTMLDivElement>) => {
    const s = start.current;
    start.current = null;
    if (!s || s.id !== e.pointerId) return;
    const ms = Math.round(performance.now() - s.t);
    const [x, y] = local(e);
    const moved = Math.hypot(x - s.x, y - s.y) > MOVE_PX;
    if (moved) {
      const n = normalize(e.currentTarget, e.clientX, e.clientY) ?? clampNorm(e.currentTarget, e.clientX, e.clientY);
      sendInput(serial, { kind: 'swipe', x: s.nx, y: s.ny, x2: n[0], y2: n[1], ms: Math.max(100, Math.min(3000, ms)) });
      ripple({ x: s.x, y: s.y, x2: x, y2: y, kind: 'swipe' });
    } else if (ms >= LONG_MS) {
      sendInput(serial, { kind: 'longpress', x: s.nx, y: s.ny, ms: Math.min(5000, ms) });
      ripple({ x: s.x, y: s.y, kind: 'long' });
    } else {
      sendInput(serial, { kind: 'tap', x: s.nx, y: s.ny });
      ripple({ x: s.x, y: s.y, kind: 'tap' });
    }
  };

  const key = (k: 'BACK' | 'HOME' | 'APP_SWITCH' | 'ENTER' | 'DEL') => sendInput(serial, { kind: 'key', key: k });
  const sendText = (e: FormEvent) => {
    e.preventDefault();
    if (!text) return;
    sendInput(serial, { kind: 'text', text });
    setText('');
  };
  const pair = async () => {
    setPairing(true);
    await sendCmd(serial, 'inputPair', {});
    setPairing(false);
  };

  return (
    <>
      <div
        ref={ref}
        className="screen-ctl"
        role="application"
        aria-label={t('screenCtl.aria')}
        onPointerDown={onDown}
        onPointerUp={onUp}
        onPointerCancel={() => (start.current = null)}
        onContextMenu={(e) => e.preventDefault()}
      >
        {ripples.map((r) =>
          r.kind === 'swipe' ? (
            <svg key={r.id} className="screen-swipe" width="100%" height="100%">
              <line x1={r.x} y1={r.y} x2={r.x2} y2={r.y2} stroke="#F0A04B" strokeWidth="3" strokeLinecap="round" />
              <circle cx={r.x2} cy={r.y2} r="6" fill="#F0A04B" />
            </svg>
          ) : (
            <span key={r.id} className={`screen-ripple${r.kind === 'long' ? ' long' : ''}`} style={{ left: r.x, top: r.y }} />
          ),
        )}
      </div>
      <div className="screen-ctl-bar" data-ui>
        <button type="button" className="overlay-btn sm" onClick={() => key('BACK')} title={t('screenCtl.back')} aria-label={t('screenCtl.back')}>
          ◁
        </button>
        <button type="button" className="overlay-btn sm" onClick={() => key('HOME')} title={t('screenCtl.home')} aria-label={t('screenCtl.home')}>
          ○
        </button>
        <button type="button" className="overlay-btn sm" onClick={() => key('APP_SWITCH')} title={t('screenCtl.recent')} aria-label={t('screenCtl.recent')}>
          ▢
        </button>
        <button type="button" className={`overlay-btn sm${kbd ? ' on' : ''}`} aria-pressed={kbd} onClick={() => setKbd(!kbd)} title={t('screenCtl.keyboard')} aria-label={t('screenCtl.keyboard')}>
          ⌨
        </button>
        {kbd && (
          <form className="screen-kbd" onSubmit={sendText}>
            <input className="input sm" autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={t('screenCtl.typeText')} aria-label={t('screenCtl.typeText')} />
            <button type="submit" className="btn xs primary" disabled={!text}>
              {t('screenCtl.send')}
            </button>
            <button type="button" className="btn xs" onClick={() => key('ENTER')} title="Enter">
              ↵
            </button>
            <button type="button" className="btn xs" onClick={() => key('DEL')} title="Backspace">
              ⌫
            </button>
          </form>
        )}
      </div>
      {input === 'needs_consent' && (
        <div className="screen-ctl-banner" role="alert" data-ui>
          <span>{t('screenCtl.needsConsent')}</span>
          <button type="button" className="btn xs" disabled={pairing} onClick={() => void pair()}>
            {t('screenCtl.askAgain')}
          </button>
        </div>
      )}
      {input === 'unavailable' && (
        <div className="screen-ctl-banner err" role="alert" data-ui>
          {t('screenCtl.unavailable')}
        </div>
      )}
    </>
  );
}

function clampNorm(overlay: HTMLElement, clientX: number, clientY: number): [number, number] {
  const r = overlay.getBoundingClientRect();
  const x = Math.min(r.right - 1, Math.max(r.left + 1, clientX));
  const y = Math.min(r.bottom - 1, Math.max(r.top + 1, clientY));
  return normalize(overlay, x, y) ?? [0.5, 0.5];
}
